/**
 * De taal waarin getallen getoond worden. Bewust een setter en geen import van
 * de i18n-instantie: dit bestand wordt ook buiten een draaiende app gebruikt
 * (tests, en `tests/setup.ts` stubt `vue-i18n`), en een import zou die allemaal
 * laten omvallen. `src/i18n/index.ts` roept dit aan zodra de taal wijzigt.
 */
let displayLocale = 'en';

export function setDisplayLocale(locale: string): void {
  displayLocale = locale === 'nl' ? 'nl' : 'en';
}

function currentLocale(): string {
  return displayLocale;
}

/**
 * Formats a number to show only necessary decimal places
 * Returns whole numbers without decimals, and decimal numbers with trailing zeros removed
 *
 * There is no single right answer for "there is no number here": a serving size of
 * nothing reads as "0 g", a metric this tenant does not track reads as "-". So the
 * caller says which, and the rule itself stays in one place.
 *
 * @param value - The number to format
 * @param fallback - What to render when there is no usable number (default: empty)
 * @param maxDecimals - Hoeveel decimalen maximaal. Blijft 3 omdat gewichten
 *   (1,25 kg schijfjes) en porties die nodig hebben; meetwaarden gebruiken
 *   `formatMeasurement` hieronder.
 * @returns Formatted number string
 *
 * @example
 * formatNumber(5) → "5"
 * formatNumber(5.0) → "5"
 * formatNumber(5.5) → "5.5"
 * formatNumber(5.50) → "5.5"
 * formatNumber(5.123) → "5.123"
 * formatNumber(5.123, "", 1) → "5.1"
 * formatNumber(null) → ""
 * formatNumber(null, "0") → "0"
 * formatNumber(0, "-") → "0"   // a logged zero is a measurement, never a fallback
 */
export function formatNumber(
  value: string | number | null | undefined,
  fallback = '',
  maxDecimals = 3,
): string {
  if (value == null) return fallback;

  // Convert to number and handle potential string input
  const num = Number(value);

  // Check if it's a valid number
  if (isNaN(num)) return fallback;

  // Use a small epsilon to handle floating point precision issues
  const EPSILON = 1e-9;

  // If the number is effectively a whole number (very close to an integer)
  if (Math.abs(num - Math.round(num)) < EPSILON) {
    return Math.round(num).toString();
  }

  // Round to a reasonable number of decimal places
  const roundedNum = Number(num.toFixed(maxDecimals));

  // Remove trailing zeros after the decimal point
  return roundedNum.toString().replace(/\.?0+$/, '');
}

/**
 * Een gemeten waarde zoals een gebruiker hem hoort te zien: hoogstens één
 * decimaal, geen decimaal als er niets achter de komma staat, en het
 * scheidingsteken van de actieve taal (nl → komma). Geldt voor metingen,
 * gewoonten en het voortgangsrapport: die waarden komen uit een apparaat of een
 * berekening, waar de derde decimaal niets zegt (Body Battery 54,752% is
 * gewoon 54,8%).
 *
 * Dit is dezelfde regel als `formatNumber`, met een andere grens en het
 * scheidingsteken erbij. Het rapport had hier zijn eigen kopie van; die is
 * hierheen gehaald zodat het metriekscherm en het rapport niet los van elkaar
 * kunnen gaan lopen. `tests/unit/utils/numberHelpers.test.ts` bewaakt dat.
 *
 * @example
 * formatMeasurement(54.752)       → "54,8" (nl) / "54.8" (en)
 * formatMeasurement(50.251)       → "50,3"
 * formatMeasurement(87)           → "87"
 * formatMeasurement(1234.5, "en") → "1,234.5"
 */
export function formatMeasurement(
  value: string | number | null | undefined,
  locale: string = currentLocale(),
  maxDecimals = 1,
): string {
  if (value == null) return '';

  const num = Number(value);
  if (isNaN(num)) return '';

  return new Intl.NumberFormat(locale === 'nl' ? 'nl-NL' : 'en-GB', {
    maximumFractionDigits: maxDecimals,
  }).format(num);
}

/**
 * How much of a habit's goal is reached, from 0 to 1. The habit ring, the habit
 * detail bar and the Gewoonten summary all read this. Without a goal (0 or
 * empty) any logged value counts as done, like the backend's `value >= goal`.
 * Kept out of habitHelpers: that module loads i18n and tests mock it away.
 */
export function goalShare(
  current: string | number | null | undefined,
  goal: string | number | null | undefined,
): number {
  const value = Number(current) || 0;
  const target = Number(goal) || 0;
  if (value <= 0) return 0;
  return target > 0 ? Math.min(value / target, 1) : 1;
}

/** A goal share as a whole percentage; 100 only when the goal is reached. */
export function goalPercent(share: number): number {
  return share >= 1 ? 100 : Math.min(Math.round(share * 100), 99);
}

/**
 * Format a number with the Dutch decimal comma, showing decimals only when
 * needed (whole numbers get none, floats are rounded to `maxDecimals`). Also
 * cleans up float artifacts like 3.4000000000000004 → "3,4".
 *
 * @example
 * formatDecimal(3.4000000000000004) → "3,4"
 * formatDecimal(12)                  → "12"
 * formatDecimal(8.5, 2)             → "8,5"
 * formatDecimal(null, 1, "0")       → "0"
 */
export function formatDecimal(
  value: string | number | null | undefined,
  maxDecimals = 1,
  fallback = '',
): string {
  if (value == null) return fallback;
  const num = Number(value);
  if (isNaN(num)) return fallback;
  return num.toLocaleString('nl-NL', { maximumFractionDigits: maxDecimals });
}

/**
 * Format a gram value for macro displays: Dutch decimal comma, max 1 decimal,
 * whole numbers without decimals.
 *
 * @example
 * formatGrams(55.82) → "55,8"
 * formatGrams(55)    → "55"
 * formatGrams(null, "0") → "0"
 */
export function formatGrams(
  value: string | number | null | undefined,
  fallback = '',
): string {
  return formatDecimal(value, 1, fallback);
}
